import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  BookmarkIcon,
  EducationIcon,
  SkillsIcon,
  SystemKnowledgeIcon,
  VerifiedIcon,
  WorkIcon,
} from 'LegacyWeployDesignLanguage/icons';
import Grid from 'LegacyWeployDesignLanguage/Grid';
import Divider from 'LegacyWeployDesignLanguage/Divider';
import Avatar from 'LegacyWeployDesignLanguage/Avatar';

import Typography from 'WeployDesignLanguage/Typography';
import Button from 'WeployDesignLanguage/buttons/Button';
import CloseButton from 'WeployDesignLanguage/buttons/CloseButton';
import Tooltip from 'WeployDesignLanguage/Tooltip';

import Title from '../../common/Title';
import RolesDetails from '../../profile/roles/RolesDetails';
import Educations from '../../profile/education/Educations';
import Certifications from '../../profile/Certifications';
import EndorsedSkills from '../../profile/EndorsedSkills';
import { StyledTags } from './WeployeeSummary';
import WeployExperiences from '../../profile/WeployExperiences';

const WeployeeProfileWrapper = styled.div`
  padding: ${props => props.theme.space.double};
  color: ${props => props.theme.colors.black};
`;

const StyledHeader = styled.div`
  display: flex;
`;

const StyledWeployeeInfo = styled.div`
  padding: ${props => `0 0 ${props.theme.space.single} 0`};
  display: flex;
  flex-direction: column;
`;

const AvatarContainer = styled.div`
  margin: auto 0;
  margin-right: ${props => props.theme.space.single};
`;

const HeaderTitle = styled.h4`
  ${props => props.theme.typography.h4}
  margin: ${props => `0 0 ${props.theme.space.half} 0`};
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const RolesContainer = styled.div`
  margin-left: ${props => props.theme.space.single};
`;

const StyledInitials = styled.div`
  color: ${props => props.theme.colors.text.primaryOnDark};
  font-size: 32px;
  letter-spacing: 2px;
  line-height: 0;
`;

const StyledAvatarAndBlurb = styled.div`
  display: flex;
  max-width: 60%;
  min-width: 500px;
`;

const StyledCloseButtonWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const UNCONSCIOUS_BIAS_EXPLANATION = `Gain access to business professionals on demand, and eliminate unconscious bias.
 Weployee profiles are designed without information that may imply race, religion or gender so
 that you are empowered to make your hiring decisions based on skills and experience.`;

const WeployeeProfile = ({
  avatarColor,
  avatarUrl,
  initials,
  name,
  workExperiences,
  technicalSkills,
  systemsKnowledges,
  educations,
  certifications,
  endorsedSkills,
  weployExperiences,
  onCloseButtonClick,
  onSendJobButtonClick,
  canSendProfileHireJob,
}) => (
  <WeployeeProfileWrapper>
    { onCloseButtonClick &&
      <Grid container justify="flex-end" alignItems="center">
        <Grid item>
          <Tooltip text="Close">
            <StyledCloseButtonWrapper>
              <CloseButton onClick={onCloseButtonClick} />
            </StyledCloseButtonWrapper>
          </Tooltip>
        </Grid>
      </Grid>
    }
    <StyledHeader>
      <StyledWeployeeInfo>
        {!name && <HeaderTitle>Weployee Profile</HeaderTitle>}
        <StyledAvatarAndBlurb>
          { (initials || name) &&
            <AvatarContainer>
              <Avatar
                size={Avatar.SMALL}
                src={avatarUrl}
                text={
                  <StyledInitials>
                    {initials}
                  </StyledInitials>
                }
                backgroundColor={avatarColor}
              />
            </AvatarContainer>
          }
          <div>
            { name && <><Typography component="h1" variant="h4">{name}</Typography> <br /></> }
            { UNCONSCIOUS_BIAS_EXPLANATION }
          </div>
        </StyledAvatarAndBlurb>
      </StyledWeployeeInfo>
      {canSendProfileHireJob &&
        <div>
          <Button
            onClick={onSendJobButtonClick}
          >
            Send Job
          </Button>
        </div>
      }
    </StyledHeader>
    <Divider />
    <DetailsContainer>
      <Grid container spacing={3}>
        <Grid item lg={7}>
          { !!weployExperiences.length &&
            <Fragment>
              <Title
                name="Weploy Experience"
                icon={BookmarkIcon}
                hintText="Work experience through Weploy"
                dividerVisible={false}
              />
              <WeployExperiences weployExperiences={weployExperiences} />
              <Divider />
            </Fragment>
          }
          <Divider />
          { !!workExperiences.length &&
            <Fragment>
              <Title
                name="Experience"
                icon={WorkIcon}
                hintText="Work experience performed prior to Weploy"
                dividerVisible={false}
              />
              <RolesContainer>
                <RolesDetails workExperience={workExperiences} showTasks showAchievements />
              </RolesContainer>
              <Divider />
            </Fragment>
          }
          <Divider />
          { !!educations.length &&
            <React.Fragment>
              <Title
                name="Education"
                icon={EducationIcon}
                dividerVisible={false}
              />
              <Educations educations={educations} />
              <Divider />
            </React.Fragment>
          }
          { !!certifications.length &&
            <React.Fragment>
              <Title
                name="Certifications"
                icon={WorkIcon}
                dividerVisible={false}
              />
              <Certifications
                certifications={certifications}
              />
            </React.Fragment>
          }
        </Grid>
        <Grid item lg={5}>
          { !!endorsedSkills.length &&
            <Fragment>
              <Title
                name="Endorsements"
                icon={VerifiedIcon}
                hintText="Skills that have received endorsement from other Weployers"
                dividerVisible={false}
              />
              <EndorsedSkills endorsedSkills={endorsedSkills} />
            </Fragment>
          }
          { !!technicalSkills.length &&
            <React.Fragment>
              <Title
                name="Skills"
                icon={SkillsIcon}
                hintText="A combination of hard and soft skills"
                dividerVisible={false}
              />
              <StyledTags
                tags={technicalSkills}
                tagMaxWidth="200px"
              />
            </React.Fragment>
          }
          { !!systemsKnowledges.length &&
            <React.Fragment>
              <Title
                name="Software and Tools"
                icon={SystemKnowledgeIcon}
                hintText="Software, systems and online platforms"
                dividerVisible={false}
              />
              <StyledTags
                tags={systemsKnowledges}
                tagMaxWidth="200px"
              />
            </React.Fragment>
          }
        </Grid>
      </Grid>
    </DetailsContainer>
  </WeployeeProfileWrapper>
);

WeployeeProfile.propTypes = {
  avatarColor: PropTypes.oneOf(Avatar.COLORS),
  initials: PropTypes.string,
  avatarUrl: PropTypes.string,
  name: PropTypes.string,
  workExperiences: PropTypes.arrayOf(PropTypes.object),
  technicalSkills: PropTypes.arrayOf(PropTypes.string),
  systemsKnowledges: PropTypes.arrayOf(PropTypes.string),
  educations: PropTypes.arrayOf(PropTypes.object),
  certifications: PropTypes.arrayOf(PropTypes.object),
  endorsedSkills: PropTypes.arrayOf(PropTypes.object),
  weployExperiences: PropTypes.arrayOf(PropTypes.object),
  onCloseButtonClick: PropTypes.func,
  onSendJobButtonClick: PropTypes.func,
  canSendProfileHireJob: PropTypes.bool,
};

WeployeeProfile.defaultProps = {
  avatarColor: Avatar.GREEN,
  initials: '',
  avatarUrl: '',
  name: '',
  workExperiences: [],
  technicalSkills: [],
  systemsKnowledges: [],
  educations: [],
  certifications: [],
  endorsedSkills: [],
  weployExperiences: [],
  onCloseButtonClick: null,
  onSendJobButtonClick: () => {},
  canSendProfileHireJob: false,
};

export default WeployeeProfile;
